/**
 * The Reader – Main Application Entry Point
 * Orchestriert alle Module und verkabelt die UI-Events.
 */

import './styles/main.css';
import './styles/text-display.css';
import './styles/controls.css';
import './styles/settings.css';
import './styles/sessions.css';

import { processText, detectFormat, processDocx } from './modules/textProcessor.js';
import { segmentText, getTotalCharCount } from './modules/textSegmenter.js';
import * as ttsService from './modules/ttsService.js';
import * as audioCache from './modules/audioCache.js';
import * as playbackController from './modules/playbackController.js';
import * as settingsManager from './modules/settingsManager.js';
import * as usageTracker from './modules/usageTracker.js';
import * as sessionManager from './modules/sessionManager.js';
import * as syncManager from './modules/sync/syncManager.js';
import { initSyncPanel } from './modules/sync/syncPanel.js';

/** @type {import('./modules/textSegmenter.js').Segment[]} */
let currentSegments = [];

/** Current session ID (if loaded from a saved session) */
let currentSessionId = null;

/** Raw input text of the current text being displayed */
let currentRawText = '';

/** Custom title for the current session (e.g. filename from upload) */
let currentSessionTitle = null;

// ============================================================
// Initialization
// ============================================================

document.addEventListener('DOMContentLoaded', async () => {
  settingsManager.load();
  usageTracker.load();
  playbackController.init();

  await applyRetention();

  trackHeaderHeight();
  initTabs();
  initInputArea();
  initFileUpload();
  initSettingsPanel();
  initPlaybackControls();
  initPreloadControl();
  initUsageDisplay();
  initTextSizeControl();
  updateSettingsUI();
  updateUsageDisplay();
  initWelcomeModal();
  await initSessionsOverview();
  initSessionsPanel();
  initAppInfoToggle();
  initAppUpdate();
  syncManager.onSynced(handleSynced);
  await syncManager.init();
  initSyncPanel({ showToast });

  window.addEventListener('resize', updateMinimapVisibility);
  window.addEventListener('scroll', updateMinimapViewport);
  initMinimap();

  // Register Service Worker for PWA.
  // Build-Version im Query-String, damit nach einem Deploy garantiert die
  // frische sw.js geladen wird (umgeht Server- und HTTP-Caches).
  // Im Dev-Modus deaktiviert – der Cache-first-SW würde sonst frisch
  // editierte Module verdecken.
  if ('serviceWorker' in navigator && !import.meta.env.DEV) {
    const swVersion =
      typeof __BUILD_TIME__ !== 'undefined' ? encodeURIComponent(__BUILD_TIME__) : 'dev';
    navigator.serviceWorker
      .register(`/sw.js?v=${swVersion}`)
      .then((reg) => console.log('Service Worker registered:', reg.scope))
      .catch((err) => console.error('Service Worker registration failed:', err));
  }
});

// ============================================================
// Aufbewahrung
// ============================================================

/**
 * Löscht beim Start alle Sitzungen und Audiodateien, die älter sind als die
 * eingestellte Aufbewahrungsdauer, und abgelaufene Löschmarkierungen.
 */
async function applyRetention() {
  sessionManager.purgeTombstones().catch((error) => {
    console.error('Löschmarkierungen konnten nicht entfernt werden:', error);
  });

  const days = Number(settingsManager.get('retentionDays'));
  if (!days || days <= 0) return;

  try {
    const [sessions] = await Promise.all([
      sessionManager.deleteOlderThan(days),
      audioCache.deleteOlderThan(days),
    ]);
    if (sessions > 0) {
      const what = sessions === 1 ? '1 Sitzung wurde' : `${sessions} Sitzungen wurden`;
      showToast(
        `${what} nach ${days} ${days === 1 ? 'Tag' : 'Tagen'} automatisch gelöscht.`,
        'info'
      );
    }
  } catch (error) {
    console.error('Aufbewahrung konnte nicht angewendet werden:', error);
  }
}

// ============================================================
// Synchronisation
// ============================================================

/**
 * Zeigt an, was ein Abgleich auf diesem Gerät geändert hat
 * @param {{ sessionIds: string[], settings: boolean, usage: boolean }} changes
 */
async function handleSynced({ sessionIds, settings, usage }) {
  if (settings) updateSettingsUI();
  if (settings || usage) updateUsageDisplay();
  if (sessionIds.length === 0) return;

  if (
    sessionIds.includes(currentSessionId) &&
    !(await sessionManager.getSession(currentSessionId))
  ) {
    showToast('Diese Sitzung wurde auf einem anderen Gerät gelöscht.', 'info');
    await goBackToStartPage();
    return;
  }
  await renderSessionsOverview();
  if (document.getElementById('sessions-panel')?.classList.contains('open')) {
    await renderSessionsPanelList();
  }
}

// ============================================================
// Tab Navigation
// ============================================================

function initTabs() {
  const tabs = document.querySelectorAll('.tab');
  tabs.forEach((tab) => {
    tab.addEventListener('click', () => {
      const targetTab = tab.dataset.tab;

      tabs.forEach((t) => t.classList.remove('active'));
      tab.classList.add('active');

      document.querySelectorAll('.tab-content').forEach((tc) => tc.classList.remove('active'));
      document.getElementById(`tab-content-${targetTab}`)?.classList.add('active');
    });
  });
}

// ============================================================
// Input Area
// ============================================================

async function goBackToStartPage() {
  playbackController.stop();
  closeSessionsPanel();
  showReadingView(false);
  enablePlaybackControls(false);
  currentSessionId = null;
  currentRawText = '';
  currentSessionTitle = null;

  // Clear input and reset counts
  const textInput = document.getElementById('text-input');
  const charCount = document.getElementById('input-char-count');
  const formatBadge = document.getElementById('format-badge');
  if (textInput) {
    textInput.value = '';
  }
  if (charCount) {
    charCount.textContent = '0 Zeichen';
  }
  if (formatBadge) {
    formatBadge.style.display = 'none';
  }

  // Reset user toggled state so it goes back to default collapsed state if sessions exist
  const card = document.getElementById('app-info-card');
  if (card) delete card.dataset.userToggled;

  await renderSessionsOverview();
}

function initInputArea() {
  const textInput = document.getElementById('text-input');
  const charCount = document.getElementById('input-char-count');
  const btnProcess = document.getElementById('btn-process');
  const formatBadge = document.getElementById('format-badge');
  const formatBadgeText = document.getElementById('format-badge-text');

  // Character count
  textInput?.addEventListener('input', () => {
    const len = textInput.value.length;
    charCount.textContent = `${len.toLocaleString('de-DE')} Zeichen`;

    // Detect and show format
    if (len > 10) {
      const format = detectFormat(textInput.value);
      formatBadgeText.textContent =
        format === 'markdown' ? 'Markdown' : format === 'html' ? 'HTML' : 'Text';
      formatBadge.style.display = 'inline-flex';
    } else {
      formatBadge.style.display = 'none';
    }
  });

  // Process button
  btnProcess?.addEventListener('click', async () => {
    const text = textInput?.value?.trim();
    if (!text) {
      showToast('Bitte fügen Sie Text ein.', 'warning');
      return;
    }
    currentSessionId = null;
    currentSessionTitle = null;
    currentRawText = text;
    await processAndDisplay(text);
  });

  // Paste from clipboard button
  document.getElementById('btn-paste-clipboard')?.addEventListener('click', async () => {
    const clipText = await readClipboardText();
    if (!clipText) return;
    // Insert at cursor position (replaces selection if any)
    const start = textInput.selectionStart ?? textInput.value.length;
    const end = textInput.selectionEnd ?? textInput.value.length;
    textInput.setRangeText(clipText, start, end, 'end');
    textInput.dispatchEvent(new Event('input'));
    textInput.focus();
  });

  // Back to input button
  document.getElementById('btn-back-to-input')?.addEventListener('click', goBackToStartPage);

  // Reading controls in the header
  document.getElementById('btn-edit-text')?.addEventListener('click', editCurrentText);
  document
    .getElementById('btn-replace-from-clipboard')
    ?.addEventListener('click', replaceWithClipboard);

  // Logo button in header
  const logoBtn = document.getElementById('logo-btn');
  if (logoBtn) {
    logoBtn.addEventListener('click', goBackToStartPage);
    logoBtn.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        goBackToStartPage();
      }
    });
  }
}

/**
 * Switches between the start page and the reading view, including the
 * reading controls and the sessions button in the header
 * @param {boolean} isReading
 */
function showReadingView(isReading) {
  document.getElementById('input-section').style.display = isReading ? 'none' : '';
  document.getElementById('display-section').style.display = isReading ? '' : 'none';
  document.getElementById('header-reading').hidden = !isReading;
  document.getElementById('btn-open-sessions').style.display = isReading ? '' : 'none';
  if (isReading) {
    document.getElementById('sessions-section').style.display = 'none';
  }
  updateMinimapVisibility();
}

/**
 * Reads text from the clipboard; explains in a toast why nothing came back
 * @returns {Promise<string|null>}
 */
async function readClipboardText() {
  if (!navigator.clipboard?.readText) {
    showToast('Zwischenablage wird von diesem Browser nicht unterstützt.', 'warning');
    return null;
  }
  try {
    const text = await navigator.clipboard.readText();
    if (!text?.trim()) {
      showToast('Die Zwischenablage ist leer.', 'info');
      return null;
    }
    return text;
  } catch {
    showToast('Zugriff auf die Zwischenablage verweigert. Bitte manuell einfügen.', 'warning');
    return null;
  }
}

/**
 * Replaces the text of the current session with the clipboard content,
 * without going through the start page
 */
async function replaceWithClipboard() {
  const text = await readClipboardText();
  if (!text) return;

  const html = processText(text);
  const segments = segmentText(html);
  if (segments.length === 0) {
    showToast('In der Zwischenablage steht kein lesbarer Text.', 'warning');
    return;
  }

  if (currentSessionId) {
    try {
      const replaced = await sessionManager.replaceSessionText(
        currentSessionId,
        text,
        html,
        segments
      );
      // Sitzung gibt es nicht mehr (z. B. in anderem Tab gelöscht): neu anlegen lassen
      if (!replaced) currentSessionId = null;
    } catch (error) {
      showToast(`Sitzung konnte nicht gespeichert werden: ${error.message}`, 'warning');
    }
  }
  currentRawText = text;
  await processAndDisplayHtml(html);
  window.scrollTo({ top: 0, behavior: 'smooth' });
  showToast('Text aus der Zwischenablage übernommen.', 'success');
}

/**
 * Keeps --header-height in sync with the real header height, which grows
 * to two rows on narrow screens; toasts and the minimap sit below it
 */
function trackHeaderHeight() {
  const header = document.getElementById('app-header');
  const root = document.documentElement;
  new ResizeObserver(() => {
    root.style.setProperty('--header-height', `${header.offsetHeight}px`);
  }).observe(header);
}

/**
 * Whether the currently displayed text can be edited
 * (docx uploads only have a placeholder as raw text)
 * @returns {boolean}
 */
function canEditCurrentText() {
  return Boolean(currentRawText) && !currentRawText.startsWith('[Datei:');
}

/**
 * Shows/hides the edit button depending on whether the current text is editable
 */
function updateEditButtonVisibility() {
  const btnEdit = document.getElementById('btn-edit-text');
  if (btnEdit) btnEdit.style.display = canEditCurrentText() ? '' : 'none';
}

/**
 * Switches back to the input view with the current text preloaded for editing
 */
async function editCurrentText() {
  if (!canEditCurrentText()) return;
  const text = currentRawText;

  playbackController.stop();
  closeSessionsPanel();
  showReadingView(false);
  enablePlaybackControls(false);

  currentSessionId = null;
  currentSessionTitle = null;

  // Switch to paste tab and preload the text into the input
  document.querySelector('.tab[data-tab="paste"]')?.click();
  const textInput = document.getElementById('text-input');
  if (textInput) {
    textInput.value = text;
    textInput.dispatchEvent(new Event('input'));
    textInput.focus();
  }

  await renderSessionsOverview();
}

// ============================================================
// File Upload (Drag & Drop)
// ============================================================

function initFileUpload() {
  const dropZone = document.getElementById('drop-zone');
  const fileInput = document.getElementById('file-input');

  if (!dropZone || !fileInput) return;

  // Drag & Drop events
  ['dragenter', 'dragover'].forEach((event) => {
    dropZone.addEventListener(event, (e) => {
      e.preventDefault();
      dropZone.classList.add('drag-over');
    });
  });

  ['dragleave', 'drop'].forEach((event) => {
    dropZone.addEventListener(event, (e) => {
      e.preventDefault();
      dropZone.classList.remove('drag-over');
    });
  });

  dropZone.addEventListener('drop', (e) => {
    const file = e.dataTransfer?.files?.[0];
    if (file) handleFile(file);
  });

  // Click to browse
  dropZone.addEventListener('click', () => fileInput.click());
  fileInput.addEventListener('change', () => {
    const file = fileInput.files?.[0];
    if (file) {
      handleFile(file);
      fileInput.value = '';
    }
  });
}

/**
 * Handles an uploaded file
 * @param {File} file
 */
async function handleFile(file) {
  const ext = file.name.split('.').pop()?.toLowerCase();

  showLoading(`${file.name} wird verarbeitet...`);

  // Ensure we start a new session for the uploaded file and name it after the file
  currentSessionId = null;
  currentSessionTitle = file.name;

  try {
    let html;
    if (ext === 'docx') {
      const buffer = await file.arrayBuffer();
      html = await processDocx(buffer);
      currentRawText = `[Datei: ${file.name}]`;
    } else {
      const text = await file.text();
      currentRawText = text;
      const format =
        ext === 'md' || ext === 'markdown'
          ? 'markdown'
          : ext === 'html' || ext === 'htm'
            ? 'html'
            : 'text';
      html = processText(text, format);
    }

    hideLoading();
    await processAndDisplayHtml(html);
  } catch (error) {
    hideLoading();
    showToast(`Fehler beim Verarbeiten: ${error.message}`, 'error');
  }
}

// ============================================================
// Text Processing & Display
// ============================================================

/**
 * Processes raw text input and displays segments
 * @param {string} text
 */
async function processAndDisplay(text) {
  const html = processText(text);
  currentRawText = text;
  await processAndDisplayHtml(html);
}

/**
 * Takes processed HTML and segments it for display
 * @param {string} html
 */
async function processAndDisplayHtml(html) {
  currentSegments = segmentText(html);

  if (currentSegments.length === 0) {
    showToast('Kein lesbarer Text gefunden.', 'warning');
    return;
  }

  showReadingView(true);
  updateSegmentCountDisplay();
  renderSegments(currentSegments);
  renderMinimap(currentSegments);
  updateMinimapVisibility();

  // Save as session (only for new texts, not when loading from session)
  if (!currentSessionId) {
    try {
      const session = await sessionManager.saveSession(
        currentRawText,
        html,
        currentSegments,
        currentSessionTitle
      );
      currentSessionId = session.id;
      currentSessionTitle = null; // Reset after saving
    } catch (e) {
      showToast(e.message, 'warning');
    }
  }

  // Setup playback
  playbackController.setSegments(currentSegments);
  enablePlaybackControls(true);
  updateEditButtonVisibility();
}

/**
 * Renders segments into the display container
 * @param {import('./modules/textSegmenter.js').Segment[]} segments
 */
function renderSegments(segments) {
  const container = document.getElementById('segments-container');
  if (!container) return;
  container.innerHTML = '';

  segments.forEach((segment, index) => {
    const el = document.createElement('div');
    el.dataset.index = index;
    el.id = segment.id;

    // Determine split-part position classes
    const prev = segments[index - 1];
    const next = segments[index + 1];
    const isPrevSplit = prev?.isSplitPart;
    const isNextSplit = next?.isSplitPart;

    if (segment.isSplitPart) {
      // Determine position within the split group
      if (!isPrevSplit && isNextSplit) {
        el.className = 'segment segment--split-start';
      } else if (isPrevSplit && isNextSplit) {
        el.className = 'segment segment--split-middle';
      } else if (isPrevSplit && !isNextSplit) {
        el.className = 'segment segment--split-end';
      } else {
        // Isolated single split (rare edge case)
        el.className = 'segment segment--split-start segment--split-end';
      }
    } else {
      el.className = 'segment';
    }

    // Content
    const contentDiv = document.createElement('div');
    if (segment.type === 'heading') {
      const headingClass = `segment-heading h${segment.headingLevel}`;
      contentDiv.className = headingClass;
      contentDiv.textContent = segment.plainText;
    } else if (segment.type === 'heading-with-text') {
      // Combined heading + paragraph: render the full HTML
      contentDiv.className = 'segment-text';
      contentDiv.innerHTML = segment.html;
    } else {
      contentDiv.className = 'segment-text';
      contentDiv.innerHTML = segment.html;
    }
    el.appendChild(contentDiv);

    // Meta (cache indicator + char count)
    const metaDiv = document.createElement('div');
    metaDiv.className = 'segment-meta';

    const cacheSpan = document.createElement('span');
    cacheSpan.className = 'cache-indicator';
    cacheSpan.id = `cache-${segment.id}`;
    cacheSpan.textContent = '○ nicht gecacht';
    metaDiv.appendChild(cacheSpan);

    const charSpan = document.createElement('span');
    charSpan.className = 'segment-char-count';
    charSpan.textContent = `${segment.charCount} Zeichen`;
    metaDiv.appendChild(charSpan);

    el.appendChild(metaDiv);

    // Play button on segment
    const playBtn = document.createElement('button');
    playBtn.className = 'segment-play-btn';
    playBtn.title = 'Abschnitt vorlesen';
    playBtn.innerHTML = `<svg viewBox="0 0 24 24" fill="currentColor" stroke="none"><polygon points="6,3 20,12 6,21"/></svg>`;
    playBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      playbackController.playSegment(index);
    });
    el.appendChild(playBtn);

    // Click on segment to select it
    el.addEventListener('click', () => {
      playbackController.setCurrentIndex(index);
    });

    container.appendChild(el);
  });
}

/**
 * Updates cache indicators in the UI
 */
async function updateCacheIndicators() {
  const settings = settingsManager.getAll();
  for (const segment of currentSegments) {
    const indicator = document.getElementById(`cache-${segment.id}`);
    if (!indicator) continue;
    const isCached = await audioCache.has(segment.plainText, settings);
    if (isCached) {
      indicator.className = 'cache-indicator cached';
      indicator.textContent = '✓ gecacht';
    } else {
      indicator.className = 'cache-indicator';
      indicator.textContent = '○ nicht gecacht';
    }
  }
}

// ============================================================
// Playback Controls
// ============================================================

function formatTime(seconds) {
  if (isNaN(seconds) || seconds === Infinity || seconds === null) return '00:00';
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
}

function initPlaybackControls() {
  const btnPlay = document.getElementById('btn-play');
  const btnAutoplay = document.getElementById('btn-autoplay');
  const btnPrev = document.getElementById('btn-prev');
  const btnNext = document.getElementById('btn-next');
  const btnSelection = document.getElementById('btn-read-selection');
  const progressSlider = document.getElementById('audio-progress-slider');
  const progressBar = document.getElementById('timeline-progress-bar');
  const timeCurrent = document.getElementById('audio-time-current');
  const timeTotal = document.getElementById('audio-time-total');

  btnPlay?.addEventListener('click', () => playbackController.togglePlayPause());
  btnAutoplay?.addEventListener('click', () => playbackController.toggleAutoplay());
  btnPrev?.addEventListener('click', () => playbackController.prev());
  btnNext?.addEventListener('click', () => playbackController.next());

  btnSelection?.addEventListener('click', () => {
    const selection = window.getSelection()?.toString()?.trim();
    if (selection) {
      playbackController.playSelection(selection);
    } else {
      showToast('Bitte markieren Sie zuerst Text.', 'warning');
    }
  });

  // State change listener
  playbackController.onStateChange(({ state, segmentIndex }) => {
    updatePlayPauseButton(state);
    updateNavigationButtons(segmentIndex);
  });

  // Segment change listener
  playbackController.onSegmentChange(async (index) => {
    highlightSegment(index);
    await updateCacheIndicators();
  });

  // Prefetch complete listener
  playbackController.onPrefetchComplete(async () => {
    await updateCacheIndicators();
  });

  // Time update listener
  playbackController.onTimeUpdate(({ currentTime, duration, progress }) => {
    if (progressSlider) {
      progressSlider.value = progress;
      progressSlider.disabled = duration === 0;
    }
    if (progressBar) {
      progressBar.style.width = `${progress}%`;
    }
    if (timeCurrent) {
      timeCurrent.textContent = formatTime(currentTime);
    }
    if (timeTotal) {
      timeTotal.textContent = formatTime(duration);
    }
  });

  // Volume control
  const volumeSlider = document.getElementById('volume-slider');
  if (volumeSlider) {
    const savedVolume = settingsManager.get('volume');
    const initialVolume = typeof savedVolume === 'number' ? savedVolume : 1;
    volumeSlider.value = Math.round(initialVolume * 100);
    playbackController.setVolume(initialVolume);

    volumeSlider.addEventListener('input', (e) => {
      const volume = parseInt(e.target.value, 10) / 100;
      playbackController.setVolume(volume);
      settingsManager.save({ volume });
    });
  }

  // Seeking input
  progressSlider?.addEventListener('input', (e) => {
    const val = parseFloat(e.target.value);
    playbackController.seek(val);
    if (progressBar) {
      progressBar.style.width = `${val}%`;
    }
  });

  // Error listener
  playbackController.onError((message) => {
    showToast(message, 'error');
  });
}

/**
 * Initializes the "Gesamten Text vorladen" button in the playback bar
 */
function initPreloadControl() {
  const btn = document.getElementById('btn-preload');
  if (!btn) return;

  btn.addEventListener('click', async () => {
    if (playbackController.isPreloading()) {
      playbackController.cancelPreload();
      return;
    }

    setPreloadButtonState(true, 0, currentSegments.length);
    try {
      const { done, total, cancelled } = await playbackController.preloadAll((d, t) =>
        setPreloadButtonState(true, d, t)
      );
      showToast(
        cancelled
          ? `Vorladen abgebrochen – ${done} von ${total} Abschnitten geladen.`
          : `${total} Abschnitte vorgeladen.`,
        cancelled ? 'info' : 'success'
      );
    } catch (error) {
      showToast(`Vorladen fehlgeschlagen: ${error.message}`, 'error');
    } finally {
      setPreloadButtonState(false);
      await updateCacheIndicators();
    }
  });
}

/**
 * Updates the preload button's label and active state
 * @param {boolean} running
 * @param {number} [done]
 * @param {number} [total]
 */
function setPreloadButtonState(running, done = 0, total = 0) {
  const btn = document.getElementById('btn-preload');
  const label = document.getElementById('preload-label');
  if (!btn || !label) return;

  btn.classList.toggle('active', running);
  btn.title = running ? 'Vorladen abbrechen' : 'Gesamten Text vorladen';
  label.textContent = running ? `${done}/${total}` : 'Vorladen';
}

/**
 * Enables/disables playback controls
 * @param {boolean} enabled
 */
function enablePlaybackControls(enabled) {
  document.getElementById('btn-play').disabled = !enabled;
  document.getElementById('btn-autoplay').disabled = !enabled;
  document.getElementById('btn-prev').disabled = !enabled;
  document.getElementById('btn-next').disabled = !enabled;
  document.getElementById('btn-read-selection').disabled = !enabled;
  document.getElementById('btn-preload').disabled = !enabled;
  updateAutoplayButton();

  const slider = document.getElementById('audio-progress-slider');
  if (slider) {
    slider.disabled = !enabled;
    if (!enabled) {
      slider.value = 0;
      const bar = document.getElementById('timeline-progress-bar');
      if (bar) bar.style.width = '0%';
      const current = document.getElementById('audio-time-current');
      const total = document.getElementById('audio-time-total');
      if (current) current.textContent = '00:00';
      if (total) total.textContent = '00:00';
    }
  }
}

/**
 * Updates the play/pause button icon and TTS loading indicator
 * @param {string} state
 */
function updatePlayPauseButton(state) {
  const iconPlay = document.getElementById('icon-play');
  const iconPause = document.getElementById('icon-pause');
  const ttsIndicator = document.getElementById('tts-loading-indicator');
  const btnPlay = document.getElementById('btn-play');

  updateAutoplayButton();

  if (state === 'loading') {
    // Pause icon so user can cancel + show loading indicator
    iconPlay.style.display = 'none';
    iconPause.style.display = '';
    ttsIndicator?.classList.add('is-loading');
    btnPlay?.classList.add('is-loading');
  } else if (state === 'playing') {
    iconPlay.style.display = 'none';
    iconPause.style.display = '';
    ttsIndicator?.classList.remove('is-loading');
    btnPlay?.classList.remove('is-loading');
  } else {
    // idle or paused
    iconPlay.style.display = '';
    iconPause.style.display = 'none';
    ttsIndicator?.classList.remove('is-loading');
    btnPlay?.classList.remove('is-loading');
  }
}

/**
 * Reflects the current autoplay mode on the autoplay button
 */
function updateAutoplayButton() {
  const btnAutoplay = document.getElementById('btn-autoplay');
  if (!btnAutoplay) return;

  const on = playbackController.isContinuous();
  btnAutoplay.classList.toggle('active', on);
  btnAutoplay.title = on ? 'Automatisches Weiterlesen ist an' : 'Automatisches Weiterlesen ist aus';
}

/**
 * Updates prev/next button states
 * @param {number} index
 */
function updateNavigationButtons(index) {
  document.getElementById('btn-prev').disabled = index <= 0;
  document.getElementById('btn-next').disabled = index >= currentSegments.length - 1;
}

/**
 * Highlights the active segment
 * @param {number} activeIndex
 */
function highlightSegment(activeIndex) {
  document.querySelectorAll('.segment').forEach((el, i) => {
    el.classList.toggle('active', i === activeIndex);
    el.classList.remove('loading');
  });

  // Scroll into view
  if (activeIndex >= 0) {
    const activeEl = document.querySelector(`.segment[data-index="${activeIndex}"]`);
    activeEl?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }

  // Update minimap active dot
  document.querySelectorAll('.minimap-dot').forEach((dot, i) => {
    dot.classList.toggle('active', i === activeIndex);
  });
}

// ============================================================
// Sessions Management
// ============================================================

/**
 * Loads and displays a saved session
 * @param {string} sessionId
 */
async function loadSession(sessionId) {
  const session = await sessionManager.getSession(sessionId);
  if (!session) {
    showToast('Sitzung nicht gefunden.', 'error');
    return;
  }

  await sessionManager.updateLastOpened(sessionId);
  currentSessionId = sessionId;
  currentRawText = session.rawText;

  // Re-segment the processed HTML
  currentSegments = segmentText(session.processedHtml);

  if (currentSegments.length === 0) {
    showToast('Sitzung enthält keinen lesbaren Text.', 'warning');
    return;
  }

  showReadingView(true);
  updateSegmentCountDisplay();
  renderSegments(currentSegments);
  renderMinimap(currentSegments);
  updateMinimapVisibility();

  playbackController.setSegments(currentSegments);
  enablePlaybackControls(true);
  updateEditButtonVisibility();
  await updateCacheIndicators();

  // Close sessions panel if open
  closeSessionsPanel();
}

/**
 * Initializes the sessions overview on the start page
 */
async function initSessionsOverview() {
  await renderSessionsOverview();
}

/**
 * Initializes the toggle behavior for the app info card
 */
function initAppInfoToggle() {
  const card = document.getElementById('app-info-card');
  const btnToggle = document.getElementById('btn-toggle-info');
  const toggleText = btnToggle?.querySelector('.toggle-text');

  if (!card || !btnToggle) return;

  btnToggle.addEventListener('click', () => {
    const isCollapsed = card.classList.toggle('collapsed');
    btnToggle.setAttribute('aria-expanded', !isCollapsed);
    if (toggleText) {
      toggleText.textContent = isCollapsed ? 'Mehr anzeigen' : 'Weniger anzeigen';
    }
    card.dataset.userToggled = 'true';
  });
}

/**
 * Renders the sessions overview on the start page
 */
async function renderSessionsOverview() {
  const section = document.getElementById('sessions-section');
  const list = document.getElementById('sessions-list');
  const countEl = document.getElementById('sessions-count');
  if (!section || !list) return;

  const sessions = await sessionManager.getAllSessions();
  const card = document.getElementById('app-info-card');
  const btnToggle = document.getElementById('btn-toggle-info');
  const toggleText = btnToggle?.querySelector('.toggle-text');

  if (sessions.length === 0) {
    section.style.display = 'none';
    if (card) {
      card.classList.remove('collapsed');
      if (btnToggle) {
        btnToggle.setAttribute('aria-expanded', 'true');
        if (toggleText) toggleText.textContent = 'Weniger anzeigen';
      }
      delete card.dataset.userToggled;
    }
    return;
  }

  section.style.display = '';
  if (card && !card.dataset.userToggled) {
    card.classList.add('collapsed');
    if (btnToggle) {
      btnToggle.setAttribute('aria-expanded', 'false');
      if (toggleText) toggleText.textContent = 'Mehr anzeigen';
    }
  }
  countEl.textContent = `${sessions.length} Sitzung${sessions.length !== 1 ? 'en' : ''}`;

  list.innerHTML = '';

  sessions.forEach((session) => {
    const sessionCard = document.createElement('div');
    sessionCard.className = 'session-card';
    sessionCard.dataset.sessionId = session.id;

    sessionCard.innerHTML = `
      <div class="session-icon">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8Z"/>
          <path d="M14 2v6h6"/>
          <path d="M16 13H8"/>
          <path d="M16 17H8"/>
          <path d="M10 9H8"/>
        </svg>
      </div>
      <div class="session-content">
        <div class="session-card-title">
          <span>${escapeHtml(session.title)}</span>
          <button class="btn-edit-title" title="Titel bearbeiten">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
               <path d="M12 20h9"/>
               <path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z"/>
            </svg>
          </button>
        </div>
        <div class="session-meta">
          <span>${sessionManager.formatDate(session.lastOpenedAt)}</span>
          <span class="session-meta-separator">·</span>
          <span>${session.totalChars.toLocaleString('de-DE')} Zeichen</span>
          <span class="session-meta-separator">·</span>
          <span>${session.segmentCount} Abschnitte</span>
        </div>
      </div>
      <div class="session-actions">
        <button class="session-btn-delete" title="Sitzung löschen" data-session-id="${session.id}">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M3 6h18"/>
            <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"/>
            <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"/>
          </svg>
        </button>
      </div>
    `;

    // Click on card to open session
    sessionCard.addEventListener('click', async (e) => {
      // Don't open if delete or edit button was clicked
      if (e.target.closest('.session-btn-delete') || e.target.closest('.btn-edit-title')) return;
      await loadSession(session.id);
    });

    // Edit title button
    const editBtn = sessionCard.querySelector('.btn-edit-title');
    editBtn.addEventListener('click', async (e) => {
      e.stopPropagation();
      const newTitle = prompt('Sitzungstitel bearbeiten:', session.title);
      if (newTitle !== null) {
        const trimmed = newTitle.trim();
        if (trimmed && trimmed !== session.title) {
          await sessionManager.updateSessionTitle(session.id, trimmed);
          await renderSessionsOverview();

          // Sync with panel if open
          const panel = document.getElementById('sessions-panel');
          if (panel && panel.classList.contains('open')) {
            await renderSessionsPanelList();
          }
        }
      }
    });

    // Delete button with confirmation
    const deleteBtn = sessionCard.querySelector('.session-btn-delete');
    let deleteConfirmTimeout = null;

    deleteBtn.addEventListener('click', async (e) => {
      e.stopPropagation();

      if (deleteBtn.classList.contains('confirm')) {
        // Second click: actually delete
        clearTimeout(deleteConfirmTimeout);
        await sessionManager.deleteSession(session.id);
        sessionCard.style.transition = 'all 0.3s ease';
        sessionCard.style.opacity = '0';
        sessionCard.style.transform = 'translateX(-20px)';
        setTimeout(async () => {
          await renderSessionsOverview();
        }, 300);
        showToast('Sitzung gelöscht.', 'info');
      } else {
        // First click: show confirmation
        deleteBtn.classList.add('confirm');
        deleteBtn.title = 'Klicken zum Bestätigen';
        deleteConfirmTimeout = setTimeout(() => {
          deleteBtn.classList.remove('confirm');
          deleteBtn.title = 'Sitzung löschen';
        }, 3000);
      }
    });

    list.appendChild(sessionCard);
  });
}

/**
 * Initializes the sessions slide-in panel (on the text display page)
 */
function initSessionsPanel() {
  const btnOpen = document.getElementById('btn-open-sessions');
  const btnClose = document.getElementById('btn-close-sessions');
  const overlay = document.getElementById('sessions-overlay');

  btnOpen?.addEventListener('click', openSessionsPanel);
  btnClose?.addEventListener('click', closeSessionsPanel);
  overlay?.addEventListener('click', closeSessionsPanel);
}

/**
 * Opens the sessions slide-in panel
 */
async function openSessionsPanel() {
  const overlay = document.getElementById('sessions-overlay');
  const panel = document.getElementById('sessions-panel');
  overlay?.classList.add('open');
  panel?.classList.add('open');
  await renderSessionsPanelList();
}

/**
 * Closes the sessions slide-in panel
 */
function closeSessionsPanel() {
  const overlay = document.getElementById('sessions-overlay');
  const panel = document.getElementById('sessions-panel');
  overlay?.classList.remove('open');
  panel?.classList.remove('open');
}

/**
 * Renders the sessions list inside the slide-in panel
 */
async function renderSessionsPanelList() {
  const body = document.getElementById('sessions-panel-body');
  const storageInfo = document.getElementById('sessions-storage-info');
  if (!body) return;

  const sessions = await sessionManager.getAllSessions();
  body.innerHTML = '';

  if (sessions.length === 0) {
    body.innerHTML = `
      <div class="sessions-empty">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
          <path d="M4 19.5v-15A2.5 2.5 0 0 1 6.5 2H20v20H6.5a2.5 2.5 0 0 1 0-5H20"/>
        </svg>
        <p>Noch keine Sitzungen gespeichert.</p>
      </div>
    `;
    return;
  }

  sessions.forEach((session) => {
    const item = document.createElement('div');
    item.className = 'session-panel-item';
    if (session.id === currentSessionId) {
      item.classList.add('active');
    }

    item.innerHTML = `
      <div class="session-panel-item-icon">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8Z"/>
          <path d="M14 2v6h6"/>
        </svg>
      </div>
      <div class="session-panel-item-content">
        <div class="session-panel-item-title">
          <span>${escapeHtml(session.title)}</span>
          <button class="btn-edit-title" title="Titel bearbeiten">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M12 20h9"/>
              <path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z"/>
            </svg>
          </button>
        </div>
        <div class="session-panel-item-meta">${sessionManager.formatDate(session.lastOpenedAt)} · ${session.segmentCount} Abschnitte</div>
      </div>
      <button class="session-panel-item-delete" title="Sitzung löschen">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <path d="M3 6h18"/>
          <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"/>
          <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"/>
        </svg>
      </button>
    `;

    // Click on item content to open session
    item.addEventListener('click', async (e) => {
      if (e.target.closest('.session-panel-item-delete') || e.target.closest('.btn-edit-title'))
        return;
      await loadSession(session.id);
    });

    // Edit title button
    const editBtn = item.querySelector('.btn-edit-title');
    editBtn.addEventListener('click', async (e) => {
      e.stopPropagation();
      const newTitle = prompt('Sitzungstitel bearbeiten:', session.title);
      if (newTitle !== null) {
        const trimmed = newTitle.trim();
        if (trimmed && trimmed !== session.title) {
          await sessionManager.updateSessionTitle(session.id, trimmed);
          await renderSessionsPanelList();
          await renderSessionsOverview();
        }
      }
    });

    // Delete button with confirmation
    const deleteBtn = item.querySelector('.session-panel-item-delete');
    let deleteConfirmTimeout = null;

    deleteBtn.addEventListener('click', async (e) => {
      e.stopPropagation();

      if (deleteBtn.classList.contains('confirm')) {
        // Second click: actually delete
        clearTimeout(deleteConfirmTimeout);
        const wasActive = session.id === currentSessionId;
        await sessionManager.deleteSession(session.id);
        showToast('Sitzung gelöscht.', 'info');

        if (wasActive) {
          // Go back to start page if active session was deleted
          playbackController.stop();
          closeSessionsPanel();
          showReadingView(false);
          enablePlaybackControls(false);
          currentSessionId = null;
          currentRawText = '';
          await renderSessionsOverview();
        } else {
          await renderSessionsPanelList();
        }
      } else {
        // First click: show confirmation
        deleteBtn.classList.add('confirm');
        deleteBtn.title = 'Klicken zum Bestätigen';
        deleteConfirmTimeout = setTimeout(() => {
          deleteBtn.classList.remove('confirm');
          deleteBtn.title = 'Sitzung löschen';
        }, 3000);
      }
    });

    body.appendChild(item);
  });

  // Storage info
  if (storageInfo) {
    const size = await sessionManager.getStorageSize();
    storageInfo.textContent = `${sessions.length} Sitzung${sessions.length !== 1 ? 'en' : ''} · ${sessionManager.formatStorageSize(size)} belegt`;
  }
}

/**
 * Escapes HTML special characters to prevent XSS
 * @param {string} text
 * @returns {string}
 */
function escapeHtml(text) {
  const div = document.createElement('div');
  div.textContent = text;
  return div.innerHTML;
}

// ============================================================
// Settings Panel
// ============================================================

function initSettingsPanel() {
  const btnSettings = document.getElementById('btn-settings');
  const btnClose = document.getElementById('btn-close-settings');
  const overlay = document.getElementById('settings-overlay');
  const panel = document.getElementById('settings-panel');

  const openSettings = () => {
    overlay.classList.add('open');
    panel.classList.add('open');
  };

  const closeSettings = () => {
    overlay.classList.remove('open');
    panel.classList.remove('open');
  };

  btnSettings?.addEventListener('click', openSettings);
  btnClose?.addEventListener('click', closeSettings);
  overlay?.addEventListener('click', closeSettings);

  // API Key
  const apiKeyInput = document.getElementById('api-key-input');
  const btnToggleKey = document.getElementById('btn-toggle-key');

  apiKeyInput?.addEventListener('change', () => {
    settingsManager.save({ apiKey: apiKeyInput.value });
  });

  btnToggleKey?.addEventListener('click', () => {
    apiKeyInput.type = apiKeyInput.type === 'password' ? 'text' : 'password';
  });

  // API Key Help toggle
  const btnHelp = document.getElementById('btn-api-help');
  const helpBox = document.getElementById('api-help-box');
  btnHelp?.addEventListener('click', () => {
    const isHidden = helpBox.style.display === 'none';
    helpBox.style.display = isHidden ? 'block' : 'none';
  });

  // Test Connection
  document.getElementById('btn-test-connection')?.addEventListener('click', async () => {
    const status = document.getElementById('connection-status');
    status.textContent = 'Teste Verbindung...';
    status.className = 'connection-status';

    const apiKey = document.getElementById('api-key-input')?.value;
    if (!apiKey) {
      status.textContent = '✗ Bitte geben Sie einen API-Key ein.';
      status.className = 'connection-status error';
      return;
    }

    settingsManager.save({ apiKey });
    const result = await ttsService.testConnection(apiKey);
    status.textContent = result.message;
    status.className = `connection-status ${result.success ? 'success' : 'error'}`;

    if (result.success) {
      loadVoices(apiKey);
    }
  });

  // Language Select
  document.getElementById('language-select')?.addEventListener('change', (e) => {
    settingsManager.save({ languageCode: e.target.value });
    const apiKey = settingsManager.get('apiKey');
    if (apiKey) {
      loadVoices(apiKey);
    }
  });

  // Voice Select
  document.getElementById('voice-select')?.addEventListener('change', (e) => {
    const voiceName = e.target.value;
    settingsManager.save({ voiceName });
    updateVoicePreview(voiceName);
    updateUsageDisplay();
  });

  // Speaking Rate
  document.getElementById('speaking-rate')?.addEventListener('input', (e) => {
    const rate = parseFloat(e.target.value);
    document.getElementById('rate-value').textContent = `${rate.toFixed(2)}x`;
    settingsManager.save({ speakingRate: rate });
    updateSegmentCountDisplay();
  });

  // Aufbewahrungsdauer
  const retentionInput = document.getElementById('retention-days');
  retentionInput?.addEventListener('change', () => {
    const days = Math.max(0, Math.min(3650, Math.round(Number(retentionInput.value) || 0)));
    retentionInput.value = days;
    settingsManager.save({ retentionDays: days });
    showToast(
      days > 0
        ? `Texte und Audiodateien werden ${days} ${days === 1 ? 'Tag' : 'Tage'} aufbewahrt.`
        : 'Automatisches Löschen ist ausgeschaltet.',
      'info'
    );
  });

  // Pitch
  document.getElementById('pitch')?.addEventListener('input', (e) => {
    const pitch = parseInt(e.target.value);
    document.getElementById('pitch-value').textContent = pitch > 0 ? `+${pitch}` : pitch.toString();
    settingsManager.save({ pitch });
  });
}

/**
 * Updates the settings UI from stored values
 */
function updateSettingsUI() {
  const settings = settingsManager.getAll();

  const sizes = ['small', 'normal', 'large', 'xl'];
  sizes.forEach((s) => document.body.classList.remove(`font-size-${s}`));
  document.body.classList.add(`font-size-${settings.textSize || 'normal'}`);

  const apiKeyInput = document.getElementById('api-key-input');
  if (apiKeyInput) apiKeyInput.value = settings.apiKey;

  const langSelect = document.getElementById('language-select');
  if (langSelect) langSelect.value = settings.languageCode;

  const rateInput = document.getElementById('speaking-rate');
  if (rateInput) {
    rateInput.value = settings.speakingRate;
    document.getElementById('rate-value').textContent =
      `${parseFloat(settings.speakingRate).toFixed(2)}x`;
  }

  const pitchInput = document.getElementById('pitch');
  if (pitchInput) {
    pitchInput.value = settings.pitch;
    const p = parseInt(settings.pitch);
    document.getElementById('pitch-value').textContent = p > 0 ? `+${p}` : p.toString();
  }

  const retentionInput = document.getElementById('retention-days');
  if (retentionInput) retentionInput.value = settings.retentionDays;

  // Load voices if API key is set
  if (settings.apiKey) {
    loadVoices(settings.apiKey);
  }
}

/**
 * Loads available voices from the API
 * @param {string} apiKey
 */
async function loadVoices(apiKey) {
  const voiceSelect = document.getElementById('voice-select');
  if (!voiceSelect) return;

  const languageCode = settingsManager.get('languageCode');

  try {
    const voices = await ttsService.fetchVoices(apiKey, languageCode);

    voiceSelect.innerHTML = '';

    if (voices.length === 0) {
      voiceSelect.innerHTML = '<option value="">Keine Stimmen gefunden</option>';
      return;
    }

    // Sort by tier then name
    voices.sort((a, b) => {
      const tierOrder = ['Neural2', 'WaveNet', 'Studio', 'Chirp 3 HD', 'Standard'];
      const aIdx = tierOrder.indexOf(a.tier);
      const bIdx = tierOrder.indexOf(b.tier);
      if (aIdx !== bIdx) return (aIdx === -1 ? 99 : aIdx) - (bIdx === -1 ? 99 : bIdx);
      return a.name.localeCompare(b.name);
    });

    for (const voice of voices) {
      const option = document.createElement('option');
      option.value = voice.name;
      option.textContent = `${voice.name} (${voice.tier}, ${voice.ssmlGender})`;
      voiceSelect.appendChild(option);
    }

    // Restore saved selection or select first
    const savedVoice = settingsManager.get('voiceName');
    if (savedVoice && voices.find((v) => v.name === savedVoice)) {
      voiceSelect.value = savedVoice;
    } else {
      voiceSelect.value = voices[0]?.name || '';
      // Automatische Wahl, keine Nutzerentscheidung – siehe settingsManager.save
      settingsManager.save({ voiceName: voiceSelect.value }, { byUser: false });
    }

    updateVoicePreview(voiceSelect.value);
  } catch {
    voiceSelect.innerHTML = '<option value="">Fehler beim Laden</option>';
  }
}

/**
 * Updates the voice preview badges
 * @param {string} voiceName
 */
function updateVoicePreview(voiceName) {
  const preview = document.getElementById('voice-preview');
  const tierBadge = document.getElementById('voice-tier-badge');
  const genderBadge = document.getElementById('voice-gender-badge');

  if (!preview || !voiceName) {
    preview.style.display = 'none';
    return;
  }

  const tier = ttsService.extractTier(voiceName);
  const freeQuota = settingsManager.getFreeTierQuota(tier);

  tierBadge.textContent = `${tier} · ${(freeQuota / 1_000_000).toFixed(0)} Mio. frei`;
  preview.style.display = 'flex';

  // Hide gender badge (we get it from name)
  genderBadge.style.display = 'none';
}

// ============================================================
// App Update (PWA)
// ============================================================

/**
 * Initializes the "App aktualisieren" button in the settings panel.
 * Unregisters all service workers, clears all caches and reloads the
 * page so the freshest version is fetched from the server.
 */
function initAppUpdate() {
  const btnUpdate = document.getElementById('btn-update-app');
  const buildInfo = document.getElementById('app-build-info');

  // Show build timestamp (injected by Vite at build time)
  if (buildInfo) {
    const buildTime = typeof __BUILD_TIME__ !== 'undefined' ? __BUILD_TIME__ : null;
    buildInfo.textContent = buildTime
      ? `Installierter Stand: ${new Date(buildTime).toLocaleString('de-DE')}`
      : '';
  }

  btnUpdate?.addEventListener('click', async () => {
    btnUpdate.disabled = true;
    showLoading('App wird aktualisiert...');

    try {
      // Unregister all service workers
      if ('serviceWorker' in navigator) {
        const registrations = await navigator.serviceWorker.getRegistrations();
        await Promise.all(registrations.map((reg) => reg.unregister()));
      }
      // Clear all caches (app shell + assets)
      if (window.caches) {
        const cacheKeys = await caches.keys();
        await Promise.all(cacheKeys.map((key) => caches.delete(key)));
      }
    } catch {
      // Auch bei Fehlern neu laden – der Reload holt im Zweifel die frische Version
    }

    window.location.reload();
  });
}

// ============================================================
// Usage Display
// ============================================================

function initUsageDisplay() {
  usageTracker.onUpdate(updateUsageDisplay);
}

function updateUsageDisplay() {
  const voiceName = settingsManager.get('voiceName');
  const tier = voiceName ? ttsService.extractTier(voiceName) : 'Standard';
  const stats = usageTracker.getStats(tier);

  const charsEl = document.getElementById('usage-chars');
  const costEl = document.getElementById('usage-cost');
  const barEl = document.getElementById('usage-bar');

  if (charsEl) {
    const usedFormatted = usageTracker.formatCharCount(stats.charsUsed);
    const totalFormatted = usageTracker.formatCharCount(stats.freeQuota);
    charsEl.textContent = `${usedFormatted} / ${totalFormatted} frei`;
  }

  if (costEl) {
    costEl.textContent =
      stats.cost > 0 ? `${usageTracker.formatCost(stats.cost)} (${tier})` : `$0.00 (${tier})`;
  }

  if (barEl) {
    barEl.style.width = `${Math.min(100, stats.percentage)}%`;
    barEl.className = 'usage-bar';
    if (stats.warning100) {
      barEl.classList.add('danger');
    } else if (stats.warning80) {
      barEl.classList.add('warning');
    }
  }

  // Show warnings
  if (stats.warning100) {
    showToast('⚠ Gratis-Kontingent aufgebraucht! Weitere Nutzung wird berechnet.', 'warning');
  } else if (stats.warning80) {
    showToast('⚠ 80% des Gratis-Kontingents verbraucht.', 'warning');
  }
}

// ============================================================
// Utilities
// ============================================================

/**
 * Shows a toast notification
 * @param {string} message
 * @param {'success'|'warning'|'error'|'info'} type
 */
function showToast(message, type = 'info') {
  const container = document.getElementById('toast-container');
  if (!container) return;

  const toast = document.createElement('div');
  toast.className = `toast toast-${type}`;
  toast.textContent = message;
  container.appendChild(toast);

  setTimeout(() => {
    toast.classList.add('toast-exit');
    setTimeout(() => toast.remove(), 300);
  }, 4000);
}

/**
 * Shows the loading overlay
 * @param {string} text
 */
function showLoading(text = 'Wird verarbeitet...') {
  const overlay = document.getElementById('loading-overlay');
  const loadingText = document.getElementById('loading-text');
  if (overlay) overlay.style.display = '';
  if (loadingText) loadingText.textContent = text;
}

/**
 * Hides the loading overlay
 */
function hideLoading() {
  const overlay = document.getElementById('loading-overlay');
  if (overlay) overlay.style.display = 'none';
}

/**
 * Initializes the text size cycle control
 */
function initTextSizeControl() {
  const btnTextSize = document.getElementById('btn-text-size');
  if (!btnTextSize) return;

  const sizes = ['small', 'normal', 'large', 'xl'];
  const sizeNames = {
    small: 'Klein',
    normal: 'Normal',
    large: 'Groß',
    xl: 'Sehr groß',
  };

  const applyTextSize = (size) => {
    sizes.forEach((s) => document.body.classList.remove(`font-size-${s}`));
    document.body.classList.add(`font-size-${size}`);
  };

  // Apply initial size
  const initialSize = settingsManager.get('textSize') || 'normal';
  applyTextSize(initialSize);

  btnTextSize.addEventListener('click', () => {
    const current = settingsManager.get('textSize') || 'normal';
    const currentIndex = sizes.indexOf(current);
    const nextSize = sizes[(currentIndex + 1) % sizes.length];

    settingsManager.save({ textSize: nextSize });
    applyTextSize(nextSize);
    showToast(`Textgröße: ${sizeNames[nextSize]}`, 'info');
  });
}

/**
 * Calculates estimated speech duration in seconds based on character count and configured speaking rate.
 * Average speaking speed is approx. 15 characters per second (140 WPM).
 * @param {number} totalChars
 * @returns {number} duration in seconds
 */
function calculateEstimatedDuration(totalChars) {
  const settings = settingsManager.getAll();
  const rate = parseFloat(settings.speakingRate) || 1.0;
  const charsPerSecond = 15 * rate;
  return Math.ceil(totalChars / charsPerSecond);
}

/**
 * Formats a duration in seconds to a human-readable German string.
 * @param {number} seconds
 * @returns {string}
 */
function formatDurationEstimate(seconds) {
  if (seconds < 60) {
    return `ca. ${seconds} Sek.`;
  }
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  if (secs === 0) {
    return `ca. ${mins} Min.`;
  }
  return `ca. ${mins} Min. ${secs} Sek.`;
}

/**
 * Updates the segment count and estimated reading time display at the top of the reader.
 */
function updateSegmentCountDisplay() {
  const el = document.getElementById('segment-count');
  if (!el || currentSegments.length === 0) return;

  const totalChars = getTotalCharCount(currentSegments);
  const estimatedSeconds = calculateEstimatedDuration(totalChars);
  const formattedDuration = formatDurationEstimate(estimatedSeconds);

  el.textContent = `${currentSegments.length} Abschnitte · ${totalChars.toLocaleString('de-DE')} Zeichen · ${formattedDuration}`;
}

/**
 * Initializes the welcome modal/tour for first-time starts
 */
function initWelcomeModal() {
  const overlay = document.getElementById('welcome-overlay');
  const btnSetup = document.getElementById('btn-welcome-setup');

  if (!overlay || !btnSetup) return;

  const settings = settingsManager.getAll();
  const tourCompleted = localStorage.getItem('the-reader-tour-completed');

  if (!tourCompleted && !settings.apiKey) {
    overlay.style.display = 'flex';
    setTimeout(() => {
      overlay.classList.add('open');
    }, 50);

    btnSetup.addEventListener('click', () => {
      overlay.classList.remove('open');
      setTimeout(() => {
        overlay.style.display = 'none';
      }, 300);

      localStorage.setItem('the-reader-tour-completed', 'true');

      const settingsOverlay = document.getElementById('settings-overlay');
      const settingsPanel = document.getElementById('settings-panel');
      if (settingsOverlay && settingsPanel) {
        settingsOverlay.classList.add('open');
        settingsPanel.classList.add('open');
      }

      const apiKeyInput = document.getElementById('api-key-input');
      if (apiKeyInput) {
        apiKeyInput.focus();
        apiKeyInput.classList.add('highlight-attention');
        setTimeout(() => {
          apiKeyInput.classList.remove('highlight-attention');
        }, 3000);
      }
    });
  }
}

/**
 * Renders the minimap navigation dots on the right side of the screen.
 * @param {import('./modules/textSegmenter.js').Segment[]} segments
 */
function renderMinimap(segments) {
  const ticksContainer = document.getElementById('minimap-ticks');
  if (!ticksContainer) return;

  ticksContainer.innerHTML = '';

  segments.forEach((segment, index) => {
    const dot = document.createElement('div');
    dot.className = 'minimap-dot';
    dot.dataset.index = index;
    // The height of the tick corresponds to the text size of the segment relative to others
    dot.style.flexGrow = segment.charCount;

    let titleText = `Abschnitt ${index + 1}`;
    if (segment.type === 'heading' || segment.type === 'heading-with-text') {
      const snippet = segment.plainText.substring(0, 20);
      titleText = `${snippet}${segment.plainText.length > 20 ? '...' : ''}`;
      dot.classList.add('minimap-dot--heading');
    }
    dot.setAttribute('data-title', titleText);

    dot.addEventListener('click', (e) => {
      e.stopPropagation(); // Avoid triggering track click
      playbackController.setCurrentIndex(index);
      const targetEl = document.querySelector(`.segment[data-index="${index}"]`);
      targetEl?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    });

    ticksContainer.appendChild(dot);
  });
}

/**
 * Shows/hides the minimap navigation based on text container height.
 * Minimap is shown only if the text container height is at least double the viewport height.
 */
function updateMinimapVisibility() {
  const container = document.getElementById('segments-container');
  const minimap = document.getElementById('minimap-container');
  if (!container || !minimap) return;

  const displaySection = document.getElementById('display-section');
  if (displaySection.style.display === 'none') {
    minimap.classList.remove('visible');
    return;
  }

  const containerHeight = container.offsetHeight;
  const viewportHeight = window.innerHeight;

  if (containerHeight >= viewportHeight * 2) {
    minimap.classList.add('visible');
    updateMinimapViewport(); // Trigger position update immediately
  } else {
    minimap.classList.remove('visible');
  }
}

/**
 * Initializes click handlers for the minimap container.
 */
function initMinimap() {
  const minimap = document.getElementById('minimap-container');
  if (!minimap) return;

  minimap.addEventListener('click', (e) => {
    if (e.target.classList.contains('minimap-dot')) return;
    const rect = minimap.getBoundingClientRect();
    const clickY = e.clientY - rect.top;
    const percent = Math.min(1, Math.max(0, clickY / rect.height));

    const scrollHeight = document.documentElement.scrollHeight;
    const clientHeight = document.documentElement.clientHeight;
    window.scrollTo({
      top: percent * (scrollHeight - clientHeight),
      behavior: 'smooth',
    });
  });
}

/**
 * Calculates and updates the height and position of the viewport slider on the minimap.
 */
function updateMinimapViewport() {
  const minimap = document.getElementById('minimap-container');
  const viewportIndicator = document.getElementById('minimap-viewport');
  if (!minimap || !viewportIndicator || !minimap.classList.contains('visible')) return;

  const scrollHeight = document.documentElement.scrollHeight;
  const clientHeight = document.documentElement.clientHeight;
  const scrollTop = window.scrollY;

  const mapHeight = minimap.clientHeight;

  // Proportion of document height that is visible
  const indicatorHeight = Math.max(24, (clientHeight / scrollHeight) * mapHeight);

  // Calculate scroll percent bounded to track range
  const maxScroll = scrollHeight - clientHeight;
  const scrollPercent = maxScroll > 0 ? scrollTop / maxScroll : 0;

  const indicatorTop = scrollPercent * (mapHeight - indicatorHeight);

  viewportIndicator.style.height = `${indicatorHeight}px`;
  viewportIndicator.style.top = `${indicatorTop}px`;
}
